/**
 * Account API - profile, update profile, change email/password, delete account.
 * Uses getApiBaseUrl from api.js and Bearer token for auth.
 */

import { getApiBaseUrl, getBaseHeaders } from './api';
import { resizeImageFile } from './resizeImage';

/**
 * @param {{ accessToken?: string }} options
 * @returns {Headers}
 */
function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return headers;
}

/**
 * GET /api/users/me - get current user profile
 * @param {{ accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function getAccountProfile({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/users/me`, {
        method: 'GET',
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/users/me/avatar - upload avatar image (multipart), store on server, save path in DB
 * @param {{ accessToken: string, file: File, onProgress?: (percent: number) => void }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function uploadAvatar({ accessToken, file, onProgress } = {}) {
    const base = getApiBaseUrl();
    const url = `${base}/api/users/me/avatar`;
    // Resize first (middle step) before sending the avatar to the backend.
    const resizedFile = await resizeImageFile(file);
    const formData = new FormData();
    formData.append('avatar', resizedFile);

    return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        if (accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        }

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && typeof onProgress === 'function') {
                const percent = Math.round((e.loaded / e.total) * 100);
                onProgress(percent);
            }
        });

        xhr.addEventListener('load', () => {
            let json = {};
            try {
                json = JSON.parse(xhr.responseText || '{}');
            } catch (_) {}
            if (xhr.status >= 200 && xhr.status < 300 && json.success === true && json.data) {
                resolve({ ok: true, data: json.data });
            } else {
                resolve({
                    ok: false,
                    error: json?.error?.message || `Request failed (${xhr.status})`,
                });
            }
        });

        xhr.addEventListener('error', () => {
            resolve({ ok: false, error: 'Network error' });
        });

        xhr.send(formData);
    });
}

/**
 * PATCH /api/users/me - update profile (name, avatar, bio, currency).
 *
 * `currency` is an ISO 4217 code from `src/config/currencies.json` (e.g.
 * "USD", "PKR"). The backend uppercases + validates it; passing an
 * unrecognised code returns a 400. Only fields that are actually passed
 * (i.e. `!== undefined`) are sent, so callers can update one field at a
 * time without accidentally clearing the others.
 *
 * @param {{
 *   accessToken?: string,
 *   name?: string,
 *   avatar?: string | null,
 *   bio?: string | null,
 *   currency?: string,
 * }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function updateProfile({ accessToken, name, avatar, bio, currency } = {}) {
    const base = getApiBaseUrl();
    const body = {};
    if (name !== undefined) body.name = name;
    if (avatar !== undefined) body.avatar = avatar;
    if (bio !== undefined) body.bio = bio;
    if (currency !== undefined) body.currency = currency;
    const res = await fetch(`${base}/api/users/me`, {
        method: 'PATCH',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/change-email
 * @param {{ accessToken?: string, newEmail: string, currentPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { user }, error?: string }>}
 */
export async function changeEmail({ accessToken, newEmail, currentPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/change-email`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ newEmail, currentPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/change-password
 * @param {{ accessToken?: string, currentPassword: string, newPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function changePassword({ accessToken, currentPassword, newPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/change-password`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ currentPassword, newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/set-password - for users without a password (e.g. Google-only). Fails if user already has a password.
 * @param {{ accessToken?: string, newPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function setPassword({ accessToken, newPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/set-password`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * DELETE /api/account
 * @param {{ accessToken?: string, password?: string, confirm?: string }} options - password if user has one, or confirm: "delete" if no password (e.g. Google-only)
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function deleteAccount({ accessToken, password, confirm } = {}) {
    const base = getApiBaseUrl();
    const body = {};
    if (password !== undefined) body.password = password;
    if (confirm !== undefined) body.confirm = confirm;
    const res = await fetch(`${base}/api/account`, {
        method: 'DELETE',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * GET /api/account/linked-providers - which OAuth providers are linked (e.g. Google).
 * @param {{ accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { providers: Array<{ id: string, name: string, linked: boolean }> }, error?: string }>}
 */
export async function getLinkedProviders({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/linked-providers`, {
        method: 'GET',
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/unlink-provider - unlink an OAuth provider (e.g. Google). User must have password set.
 * @param {{ accessToken?: string, provider: string }} options - provider e.g. 'google'
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function unlinkProvider({ accessToken, provider } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/unlink-provider`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: JSON.stringify({ provider }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/auth/forgot-password - request a password reset email (public, no auth needed).
 * @param {{ email: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function forgotPassword({ email } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { ...getBaseHeaders() },
        body: JSON.stringify({ email }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/auth/reset-password - reset password using token from email (public, no auth needed).
 * @param {{ token: string, userId: number, newPassword: string }} options
 * @returns {Promise<{ ok: boolean, data?: { message }, error?: string }>}
 */
export async function resetPassword({ token, userId, newPassword } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/auth/reset-password`, {
        method: 'POST',
        headers: { ...getBaseHeaders() },
        body: JSON.stringify({ token, userId, newPassword }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/connect-kb - connect Knowledge Base app
 * First time: pass creatorData to save. Reconnect: no body needed.
 * @param {{ accessToken?: string, creatorData?: object }} options
 * @returns {Promise<{ ok: boolean, data?: { kbConnected: boolean, isReconnect: boolean }, error?: string }>}
 */
export async function connectKnowledgeBase({ accessToken, creatorData } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/connect-kb`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
        body: creatorData ? JSON.stringify(creatorData) : JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}

/**
 * POST /api/account/disconnect-kb - disconnect Knowledge Base app
 * @param {{ accessToken?: string }} options
 * @returns {Promise<{ ok: boolean, data?: { kbConnected: boolean }, error?: string }>}
 */
export async function disconnectKnowledgeBase({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/account/disconnect-kb`, {
        method: 'POST',
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return { ok: false, error: json?.error?.message || `Request failed (${res.status})` };
}
